import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
type Sale = {
  name: string;
  location: string;
  time: string;
};
const SALES: Sale[] = [{
  name: 'Brandon W.',
  location: 'Austin, USA',
  time: '5 minutes ago'
}, {
  name: 'Sarah T.',
  location: 'London, UK',
  time: '12 minutes ago'
}, {
  name: 'Michael R.',
  location: 'Toronto, Canada',
  time: '3 minutes ago'
}, {
  name: 'Linda S.',
  location: 'Sydney, Australia',
  time: '15 minutes ago'
}, {
  name: 'David B.',
  location: 'New York City, USA',
  time: '8 minutes ago'
}, {
  name: 'Jennifer K.',
  location: 'Chicago, USA',
  time: '22 minutes ago'
}, {
  name: 'Robert M.',
  location: 'Melbourne, Australia',
  time: '1 minute ago'
}, {
  name: 'Elizabeth D.',
  location: 'Manchester, UK',
  time: '34 minutes ago'
}, {
  name: 'Amanda P.',
  location: 'Vancouver, Canada',
  time: '6 minutes ago'
}, {
  name: 'James L.',
  location: 'Los Angeles, USA',
  time: '10 minutes ago'
}, {
  name: 'Patricia H.',
  location: 'Miami, USA',
  time: '4 minutes ago'
}, {
  name: 'Christopher F.',
  location: 'Birmingham, UK',
  time: '18 minutes ago'
}, {
  name: 'Susan J.',
  location: 'Perth, Australia',
  time: '55 minutes ago'
}, {
  name: 'Matthew G.',
  location: 'Denver, USA',
  time: '11 minutes ago'
}, {
  name: 'Mary V.',
  location: 'Dublin, Ireland',
  time: '7 minutes ago'
}, {
  name: 'Richard C.',
  location: 'Auckland, New Zealand',
  time: '2 minutes ago'
}, {
  name: 'Barbara Q.',
  location: 'Houston, USA',
  time: '45 minutes ago'
}, {
  name: 'Steven N.',
  location: 'Brisbane, Australia',
  time: '9 minutes ago'
}, {
  name: 'Dorothy E.',
  location: 'Ottawa, Canada',
  time: '13 minutes ago'
}, {
  name: 'Thomas Y.',
  location: 'San Francisco, USA',
  time: '25 minutes ago'
}];
const LOCAL_NAMES = ['Emily R.', 'Daniel K.', 'Olivia M.', 'William S.', 'Sophia L.', 'Henry B.'];
const VISIBLE_MS = 4000;
const INTERVAL_MS = 15000;
const INITIAL_DELAY_MS = 3000;
export const SocialProofNotifications = () => {
  const [sales, setSales] = useState<Sale[]>(SALES);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const salesRef = useRef(sales);
  salesRef.current = sales;
  // Best-effort dynamic geolocation: insert one notification matching the
  // visitor's own city/country so the social proof feels local.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetch('https://ipapi.co/json/', {
      signal: controller.signal
    }).then(res => res.ok ? res.json() : null).then(data => {
      if (cancelled || !data || !data.city || !data.country_name) return;
      const localSale: Sale = {
        name: LOCAL_NAMES[Math.floor(Math.random() * LOCAL_NAMES.length)],
        location: `${data.city}, ${data.country_name}`,
        time: 'just now'
      };
      // Place the local sale near the front so the visitor sees it early.
      setSales(prev => {
        const next = [...prev];
        next.splice(1, 0, localSale);
        return next;
      });
    }).catch(() => {
      /* offline / blocked — fall back to the static dataset */
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);
  useEffect(() => {
    let hideTimeout: ReturnType<typeof setTimeout>;
    const showNext = () => {
      setVisible(true);
      hideTimeout = setTimeout(() => {
        setVisible(false);
        setIndex(i => (i + 1) % salesRef.current.length);
      }, VISIBLE_MS);
    };
    const initial = setTimeout(showNext, INITIAL_DELAY_MS);
    const interval = setInterval(showNext, INTERVAL_MS);
    return () => {
      clearTimeout(initial);
      clearTimeout(hideTimeout);
      clearInterval(interval);
    };
  }, []);
  const current = sales[index % sales.length];
  return <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-sm pointer-events-none" role="status" aria-live="polite" data-id="element-218" data-mp="8m0ajhrwbqfb">
      <AnimatePresence data-id="element-219" data-mp="ij4m6no483hv">
        {visible && current && <motion.div key={`${current.name}-${index}`} initial={{
        opacity: 0,
        y: -24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -16,
        scale: 0.96
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-familia-card/95 backdrop-blur-sm border border-familia-cyan/30 rounded-xl shadow-[0_8px_30px_-8px_rgba(0,0,0,0.6)] px-4 py-3 flex items-start gap-3" data-id="element-220" data-mp="mk80h5qilrgz">
            <div className="shrink-0 mt-0.5" data-id="element-221" data-mp="2qpgk46xdu67f">
              <CheckCircleIcon className="w-6 h-6 text-familia-green" data-id="element-222" data-mp="2qooncss1u2fk" />
            </div>
            <div className="min-w-0" data-id="element-223" data-mp="2lrf1to7s6ax0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-familia-green mb-0.5 flex items-center gap-1" data-id="element-224" data-mp="2grsegly8neah">
                ✔️ Verified Purchase
              </p>
              <p className="text-[13px] leading-snug text-white/90" data-id="element-225" data-mp="3ehn9un8lrexv">
                <span className="font-bold text-white" data-id="element-226" data-mp="39hsk4n0kcvee">{current.name}</span>{' '}
                from{' '}
                <span className="font-semibold text-familia-cyan" data-id="element-227" data-mp="34o6jjt7ochyy">
                  {current.location}
                </span>{' '}
                just purchased the Vitality 360™ Kit{' '}
                <span className="text-familia-gray" data-id="element-228" data-mp="11fwznm18hym">{current.time}</span>.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};